import { useCallback, useEffect, useMemo, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import dayjs from 'dayjs';
import { first } from 'lodash';
import Agro1Loader from '../../components/Agro1Loader';
import DropdownModal from '../../components/DropdownModal/dropdown-modal';
import printPDF from '../../utils/print-pdf';
import PendingAplicationReportDoc from './PendingAplicationReportDoc';
import DemandaUsoInsumoReportDoc from './DemandaUsoInsumoReportDoc';
import usePendingApprovesStore from './store';
import { DemandasUsoInsumoService, OperacoesService } from '../../services';
import useLocalStore from '../../hooks/use-local-store';
import aproveToast from './aproveToast';

interface PendingAproveReportModalProps {
  visible: boolean;
  onHide: () => void;
}

const RECOMENDATION_REPORT_NAME = 'Relatório de Recomendações de Pulverização';

// Cede frames para o React pintar o Agro1Loader antes de trabalho síncrono
const yieldToPaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const PendingAproveReportModal: React.FC<PendingAproveReportModalProps> = ({
  visible,
  onHide,
}) => {
  const currentPropriedade = useLocalStore('currentPropriedade');
  const { notifications } = usePendingApprovesStore();
  const [pendingReport, setPendingReport] = useState<string>('');
  const [dateRange, setDateRange] = useState<Date[] | null>(null);

  const getDefaultWeekRange = useCallback(
    () => [dayjs().toDate(), dayjs().add(7, 'day').toDate()],
    [],
  );

  const handleDateRangeChange = useCallback((dates: Date[] | null) => {
    setDateRange(dates);
  }, []);

  const generateAplicationReport = useImmutableCallback(async () => {
    const loaderId = Agro1Loader.show();
    await yieldToPaint();
    try {
      await printPDF(
        <PendingAplicationReportDoc notifications={notifications} />,
      );
    } catch {
      aproveToast.reportError();
    } finally {
      Agro1Loader.close(loaderId);
    }
  });

  const getPulverizacaoOperacao = useImmutableCallback(async () => {
    const operacoes = await OperacoesService.list();
    const pulverizacaoOperacao = first(
      operacoes.filter((op) =>
        op.descricao.toLowerCase().includes('pulverização'),
      ),
    );
    if (!pulverizacaoOperacao) {
      throw new Error(
        'Operação de pulverização não encontrada. Certifique-se de que existe uma operação com "pulverização" no nome.',
      );
    }
    return pulverizacaoOperacao;
  });

  const generateRecomendationReport = useImmutableCallback(async () => {
    if (!dateRange || dateRange.length < 2) return;

    const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
    const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');

    const loaderId = Agro1Loader.show();
    await yieldToPaint();
    try {
      const pulverizacaoOperacao = await getPulverizacaoOperacao();
      const data = await DemandasUsoInsumoService.list({
        propriedadeId: currentPropriedade.value?.id,
        operacaoId: pulverizacaoOperacao.id,
        startDate,
        endDate,
      });

      if (!data.length) {
        aproveToast.noRecords();
        return;
      }

      const now = dayjs();
      await yieldToPaint();
      await printPDF(
        <DemandaUsoInsumoReportDoc
          data={data}
          startDate={dayjs(dateRange[0]).format('DD/MM/YY')}
          endDate={dayjs(dateRange[1]).format('DD/MM/YY')}
          generatedAt={now.format('DD/MM/YY HH:mm')}
          pageUrl={window.location.href}
        />,
      );
    } catch (error) {
      aproveToast.reportError();
    } finally {
      Agro1Loader.close(loaderId);
    }
  });

  const dropdownOptions = useMemo(
    () => [
      {
        value: RECOMENDATION_REPORT_NAME,
        action: generateRecomendationReport,
        dateSelection: true,
      },
      { value: 'Relatório de Aplicações', action: generateAplicationReport },
    ],
    [generateAplicationReport, generateRecomendationReport],
  );

  const selectedReport = useMemo(
    () => dropdownOptions.find((option) => option.value === pendingReport),
    [dropdownOptions, pendingReport],
  );

  useEffect(() => {
    if (
      visible &&
      pendingReport === RECOMENDATION_REPORT_NAME &&
      (!dateRange || dateRange.length < 2)
    ) {
      setDateRange(getDefaultWeekRange());
    }
  }, [visible, pendingReport, dateRange, getDefaultWeekRange]);

  const handlePendingReportModalConfirm = useCallback(async () => {
    if (!selectedReport?.action) return;
    try {
      await selectedReport.action();
      onHide();
    } catch {
      aproveToast.reportError();
    }
  }, [selectedReport, onHide]);

  return (
    <DropdownModal
      visible={visible}
      title="Demanda de insumos para aprovações pendentes"
      label="Relatório"
      options={dropdownOptions}
      value={pendingReport}
      onValueChange={setPendingReport}
      onConfirm={handlePendingReportModalConfirm}
      onHide={onHide}
      selectedReport={selectedReport}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
    />
  );
};

export default PendingAproveReportModal;
